import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

export interface AmateurStationTooltipDetail {
  stationId:  string;
  name:       string;
  detailsUrl: string;
  qfLocator:  string | null;
  /** Screen pixel where the user clicked. */
  screenX: number;
  screenY: number;
}

/**
 * Phase 10 chunk 3 — `<sat-amateur-station-tooltip>`.
 *
 * Small pop-up that surfaces when the user clicks an amateur SatNOGS
 * ground-station violet dot. Shows the station name + Maidenhead grid
 * + a link to the SatNOGS Network page so the user can dive into
 * antennas, recent observations, etc.
 *
 * Distinct from the Phase 4 `<sat-station-tooltip>`: that one
 * surfaces a live "tracking N satellites ≥ 5°" count for the curated
 * 41-station catalog (real ground-station network operators care
 * about overhead traffic). The amateur layer is community-operated
 * SDR receivers — the relevant action is "open SatNOGS to see what
 * they're doing," not "how many sats are overhead."
 *
 * Dismisses on Escape, on outside click, or after 8 seconds.
 */
@customElement('sat-amateur-station-tooltip')
export class SatAmateurStationTooltip extends LitElement {
  @state() private current: AmateurStationTooltipDetail | null = null;
  private autoCloseTimer: number | null = null;

  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 90;
    }
    .tooltip {
      position: absolute;
      transform: translate(-50%, -100%);
      margin-top: -0.6rem;
      background: var(--color-bg-elevated);
      border: 1px solid #b366ff;
      border-radius: 4px;
      padding: 0.5rem 0.7rem;
      box-shadow: 0 6px 18px rgba(0,0,0,0.35);
      font-family: var(--font-mono);
      font-size: 0.8rem;
      color: var(--color-text);
      min-width: 220px;
      pointer-events: auto;
    }
    .tooltip__name { color: #b366ff; font-weight: 500; }
    .tooltip__grid { margin-top: 0.25rem; color: var(--color-text-muted); font-size: 0.75rem; }
    .tooltip__link { margin-top: 0.4rem; }
    .tooltip__link a { color: var(--color-accent); text-decoration: none; }
    .tooltip__link a:hover { text-decoration: underline; }
    .tooltip__hint { margin-top: 0.4rem; color: var(--color-text-dim); font-size: 0.7rem; }
    .tooltip::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-width: 6px 6px 0;
      border-style: solid;
      border-color: #b366ff transparent transparent;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('amateur-station-pick-info', this.handleEvent as EventListener);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleOutsideClick, true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('amateur-station-pick-info', this.handleEvent as EventListener);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleOutsideClick, true);
    if (this.autoCloseTimer !== null) {
      window.clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  private handleEvent = (e: CustomEvent<AmateurStationTooltipDetail>): void => {
    this.current = e.detail;
    if (this.autoCloseTimer !== null) window.clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = window.setTimeout(() => { this.current = null; }, 8000);
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.current = null;
  };

  private handleOutsideClick = (e: MouseEvent): void => {
    if (this.current === null) return;
    if (e.composedPath().includes(this)) return;
    this.current = null;
  };

  render() {
    const c = this.current;
    if (c === null) return nothing;
    return html`
      <div class="tooltip" style="left: ${c.screenX}px; top: ${c.screenY}px;">
        <div class="tooltip__name">📡 ${c.name}</div>
        ${c.qfLocator !== null ? html`
          <div class="tooltip__grid">Grid: <strong>${c.qfLocator}</strong></div>
        ` : nothing}
        <div class="tooltip__link">
          <a href=${c.detailsUrl} target="_blank" rel="noopener noreferrer">
            View on SatNOGS Network →
          </a>
        </div>
        <div class="tooltip__hint">Click elsewhere or press Esc to dismiss.</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-amateur-station-tooltip': SatAmateurStationTooltip;
  }
  interface WindowEventMap {
    'amateur-station-pick-info': CustomEvent<AmateurStationTooltipDetail>;
  }
}
